<template>
  <div>
    <v-dialog v-model="dialog"  max-width="800px">
      <v-card>
        <v-card-title class="redColor car-dialog-title d-flex align-center ga-2">
          <div class="car-dialog-title-text text-white flex-grow-1">
            <span class="text-subtitle-1 font-weight-medium d-block">
              {{ $t("vehicle_entry.car_details") }}
            </span>
            <span
              v-if="car?.brand?.title || car?.model?.fullDescription"
              class="text-body-2 d-block"
            >
              <template v-if="car?.brand?.title">{{ car.brand.title }}</template>
              <template v-if="car?.model?.fullDescription">
                {{ " " + car.model.fullDescription }}
              </template>
            </span>
          </div>
          <v-btn
            variant="outlined"
            icon
            color="white"
            class="car-dialog-close-btn flex-shrink-0"
            size="small"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="loading && !car">
          <v-row align="center" justify="center">
            <v-progress-circular indeterminate size="50" color="red-darken-2" />
          </v-row>
        </v-card-text>
        <v-card-text v-if="car">
          <v-row class="mt-4">
            <v-col cols="12">
              <v-row>
                <v-col cols="12" md="4">
                  <v-row class="flex-column">
                    <span class="caption">{{
                      $t("parts.advanced_search.version")
                    }}</span>
                    <span class="font-weight-bold">
                      {{ car.version.description }}
                    </span>
                  </v-row>
                </v-col>

                <v-col v-if="display.smAndUp">
                  <v-row class="flex-column">
                    <span class="caption">{{
                      $t("parts.advanced_search.year")
                    }}</span>
                    <span class="font-weight-bold">
                      {{ formatDate(car.version.startingDate) }} -
                      {{ formatDate(car.version.endingDate) }}
                    </span>
                  </v-row>
                </v-col>

                <v-col v-if="display.smAndUp">
                  <v-row class="flex-column">
                    <span class="caption">{{
                      $t("parts.advanced_search.body")
                    }}</span>
                    <span class="font-weight-bold">
                      {{ car.version.numberOfDoors }}
                    </span>
                  </v-row>
                </v-col>

                <v-col v-if="display.smAndUp">
                  <v-row class="flex-column">
                    <span class="caption">{{
                      $t("parts.advanced_search.power")
                    }}</span>
                    <span class="font-weight-bold">
                      {{ car.version.power.kw }} kw
                    </span>
                  </v-row>
                </v-col>

                <v-col v-if="display.smAndUp">
                  <v-row class="flex-column">
                    <span class="caption">{{
                      $t("parts.advanced_search.engine")
                    }}</span>
                    <span class="font-weight-bold">
                      {{ car.version.engineCode }}
                    </span>
                  </v-row>
                </v-col>

                <v-col v-if="display.smAndUp">
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("parts.advanced_search.supply.supply") }}
                    </span>
                    <span class="font-weight-bold">
                      {{ getSupplyLocale(car.version.supplyCode) }}
                    </span>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" class="mt-4">
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pr-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">{{ $t("vehicle_entry.targa") }}</span>
                    <span class="font-weight-medium">{{
                      car.targa || "-"
                    }}</span>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pl-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">{{
                      $t("vehicle_entry.info.frame")
                    }}</span>
                    <span class="font-weight-medium">{{ car.vin || "-" }}</span>
                  </v-row>
                </v-col>
                <v-divider class="my-2"></v-divider>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pr-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.transmission_type") }}
                    </span>
                    <span class="font-weight-medium">
                      {{ car.transmissionType || "-" }}
                    </span>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pl-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.transmission_layout") }}
                    </span>
                    <span class="font-weight-medium">
                      {{ car.transmissionLayout || "-" }}
                    </span>
                  </v-row>
                </v-col>
                <v-divider class="my-2"></v-divider>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pr-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.external_color") }}
                    </span>
                    <span class="font-weight-medium">
                      {{ car.externalColor || "-" }}
                    </span>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pl-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.internal_color") }}
                    </span>
                    <span class="font-weight-medium">
                      {{ car.internalColor || "-" }}
                    </span>
                  </v-row>
                </v-col>
                <v-divider class="my-2"></v-divider>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pr-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.internal_state") }}
                    </span>
                    <span class="font-weight-medium">
                      {{ car.internalState || "-" }}
                    </span>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pl-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.vehicle_condition") }}
                    </span>
                    <span class="font-weight-medium">
                      {{ car.vehicleCondition || "-" }}
                    </span>
                  </v-row>
                </v-col>
                <v-divider class="my-2"></v-divider>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pr-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.practice_number") }}
                    </span>
                    <span class="font-weight-medium">
                      {{ car.pra || "-" }}
                    </span>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pl-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.number_of_km") }}
                    </span>
                    <span class="font-weight-medium">
                      {{ car.km || "-" }}
                    </span>
                  </v-row>
                </v-col>
                <v-divider class="my-2"></v-divider>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pr-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.location") }}
                    </span>
                    <span class="font-weight-medium">
                      <!-- {{
                        carLocations.find((l) => l._id === car.location._id)
                          ?.name || "-"
                      }} -->
                      {{
                        car.location && car.location.name
                          ? car.location.name
                          : "-"
                      }}
                      {{
                        car.location && car.location.description
                          ? " - " + car.location.description
                          : ""
                      }}
                    </span>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pl-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.notes") }}
                    </span>
                    <span class="font-weight-medium">
                      {{ car.notes || "-" }}
                    </span>
                  </v-row>
                </v-col>
                <v-divider class="my-2"></v-divider>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pr-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.registration_year") }}
                    </span>
                    <span class="font-weight-medium">
                      {{ car.registrationYear || "-" }}
                    </span>
                  </v-row>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pl-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.withdrawn_piva") }}
                    </span>
                    <span class="font-weight-medium">
                      {{
                        withdrawnPIVASelection.find(
                          (i) => i.value === car.withdrawnPIVA,
                        )?.title || "-"
                      }}
                    </span>
                  </v-row>
                </v-col>
                <v-divider class="my-2"></v-divider>
                <v-col
                  cols="12"
                  sm="6"
                  class="pb-2"
                  :class="{ 'pr-2': display.smAndUp }"
                >
                  <v-row class="flex-column">
                    <span class="caption">
                      {{ $t("vehicle_entry.info.working_engine") }}
                    </span>
                    <span class="font-weight-medium">
                      {{
                        workingEngineSelection.find(
                          (i) => i.value === car.workingEngine,
                        )?.title || "-"
                      }}
                    </span>
                  </v-row>
                </v-col>
                <v-divider class="my-2"></v-divider>
              </v-row>
            </v-col>
            <v-col
              v-for="(image, index) in car.photos"
              :key="index"
              cols="4"
              sm="3"
              class="pa-1"
            >
              <v-card
                class="d-flex align-center justify-center"
                tile
                height="120"
                width="160"
                style="position: relative; cursor: pointer"
                @mouseenter="hoveredImage = index"
                @mouseleave="hoveredImage = null"
              >
                <v-img
                  :src="`https://leko-tech.fra1.cdn.digitaloceanspaces.com/${image.thumb}`"
                  cover
                  height="100%"
                  width="100%"
                />
                <v-icon
                  v-if="hoveredImage === index"
                  color="primary"
                  size="36"
                  style="position: absolute; top: 8px; left: 8px"
                  @click="viewImage(image)"
                >
                  mdi-image
                </v-icon>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="red-darken-1" variant="outlined" @click="close">
            {{ $t("parts.inventory.form.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <img-dialog ref="imgDialog" @close="showDialog" />
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import ImgDialog from "@/components/imgDialog.vue";
import { useDisplay } from "vuetify";
import getRequest from "../api/getRequest";

export default {
  components: { ImgDialog },
  setup() {
    const display = useDisplay();
    return { display };
  },
  data: () => ({
    dialog: false,
    loading: false,
    hidden: false,
    car: null,
    carLocations: [],
    loadingCarLocations: false,
    withdrawnPIVASelection: [
      { title: "Yes", value: true },
      { title: "No", value: false },
    ],
    workingEngineSelection: [
      { title: "Yes", value: true },
      { title: "No", value: false },
    ],
    hoveredImage: null,
  }),
  watch: {
    dialog(value) {
      if (!value && !this.hidden) this.close();
    },
  },
  created() {
    this.getLocale();
  },
  methods: {
    // FORMAT DATE
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).isValid() ? moment(date).format("MM/YYYY") : "";
    },

    // OPEN/CLOSE DIALOG
    open(data) {
      this.dialog = true;
      this.loading = true;
      this.getCarLocations();
      getRequest("/car/", `id=${data.carId}`)
        .then((response) => {
          this.car = response.car;
        })
        .catch(() => {
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    close() {
      this.dialog = false;
      this.hidden = false;
      this.car = null;
      this.carLocations = [];
      this.$emit("close");
    },

    // IMAGE DIALOG
    viewImage(image) {
      const { imgDialog } = this.$refs;
      imgDialog.open({ type: "string", data: image });
      // this.hideDialog();
    },
    hideDialog() {
      this.hidden = true;
      this.dialog = false;
    },
    showDialog() {
      this.hidden = false;
      this.dialog = true;
    },

    // FETCH LOCATIONS
    getCarLocations() {
      this.loadingCarLocations = true;
      getRequest(
        "/car-location",
        `role=${this.$store.getters.role}&institution_id=${this.$store.getters.institutionId}`,
      )
        .then((response) => {
          this.carLocations = response.carLocations;
        })
        .catch(() => {
          this.loadingCarLocations = false;
        })
        .finally(() => {
          this.loadingCarLocations = false;
        });
    },

    // TRANSLATIONS
    getLocale() {
      this.workingEngineSelection[0].title = this.$t("vehicle_entry.info.yes");
      this.workingEngineSelection[1].title = this.$t("vehicle_entry.info.no");
      this.withdrawnPIVASelection[0].title = this.$t("vehicle_entry.info.yes");
      this.withdrawnPIVASelection[1].title = this.$t("vehicle_entry.info.no");
    },

    // SUPPLY LOCALIZATION
    getSupplyLocale(supply) {
      switch (supply) {
        case "B":
          return this.$t("parts.advanced_search.supply.petrol");
        case "D":
          return this.$t("parts.advanced_search.supply.diesel");
        case "G":
          return this.$t("parts.advanced_search.supply.gas");
        default:
          return supply;
      }
    },
  },
};
</script>

<style scoped>
.car-dialog-title {
  padding: 12px 16px;
}

.car-dialog-title-text {
  min-width: 0;
  line-height: 1.35;
  word-break: break-word;
}

@media (max-width: 959.98px) {
  .car-dialog-title {
    position: relative;
    padding: 12px 52px 12px 16px;
  }

  .car-dialog-close-btn {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
  }
}
</style>
